import { endpoints } from '@/api/endpoints';
import {
  acceptOfferResponseSchema,
  createApplicationResponseSchema,
  createOpenBankingUrlResponseSchema,
  getApplicationResponseSchema,
  signOfferResponseSchema,
  startApplicationAssessmentResponseSchema,
} from '@/api/SE/credit/schemas';
import type { CreateApplicationPayload, DeclineOfferOptions } from '@/api/SE/credit/types';
import { apiRequest } from '@/api/utils/api';
import type { AddonProducts } from '@/constants/AddonProduct';
import type { UtmTagData } from '@/utils/tracking';

export const createApplication = (payload: CreateApplicationPayload) =>
  apiRequest({
    url: '/se/credit/applications',
    method: 'POST',
    schema: createApplicationResponseSchema,
    data: payload,
  });

type StartApplicationAssessmentOptions = {
  applicationId: string;
  shouldAssessWithOpenBanking: boolean;
};

export const startApplicationAssessment = ({
  applicationId,
  shouldAssessWithOpenBanking,
}: StartApplicationAssessmentOptions) =>
  apiRequest({
    url: `/se/credit/applications/${applicationId}`,
    method: 'POST',
    schema: startApplicationAssessmentResponseSchema,
    data: { shouldAssessWithOpenBanking },
  });

type SignOfferOptions = {
  applicationId: string;
  addonProducts?: AddonProducts[];
  utm?: UtmTagData;
};

export const signOffer = ({ applicationId, ...payload }: SignOfferOptions) =>
  apiRequest({
    url: endpoints.SE.credit.applications.applicationId(applicationId).signOffer,
    method: 'POST',
    schema: signOfferResponseSchema,
    data: payload,
  });

export const acceptOffer = (applicationId: string) =>
  apiRequest({
    url: endpoints.SE.credit.applications.applicationId(applicationId).acceptOffer,
    method: 'POST',
    schema: acceptOfferResponseSchema,
  });

export const declineOffer = ({ applicationId, customerFeedback }: DeclineOfferOptions) =>
  apiRequest<void>({
    url: endpoints.SE.credit.applications.applicationId(applicationId).declineOffer,
    method: 'POST',
    data: { customerFeedback },
  });

export const getApplication = (applicationId: string) =>
  apiRequest({
    url: `/se/credit/applications/${applicationId}`,
    method: 'GET',
    schema: getApplicationResponseSchema,
  });

type CreateOpenBankingUrlOptions = {
  applicationId: string;
  redirectUrl: string;
};

export const createOpenBankingUrl = ({ applicationId, redirectUrl }: CreateOpenBankingUrlOptions) =>
  apiRequest({
    url: `/se/credit/applications/${applicationId}/open-banking-url`,
    method: 'POST',
    schema: createOpenBankingUrlResponseSchema,
    data: { redirectUrl },
  });
